import type { FwdReason, FwdRule, FwdState, Group, Member, MemberSync } from "../cloud/api";

/**
 * Pure line-group logic shared by the runner and the UI (Prompt 3 v3, Part A2b, Prompt 0
 * "Concurrency model"). The cloud owns desired state; these helpers let the page derive the
 * same thing so it can (a) mirror the cloud's 422 guards before making a request and
 * (b) tell the user, per member, whether the phone's read-back matches what was desired.
 */

export const MAX_MEMBERS = 5;
export const DEFAULT_NO_REPLY_TIMER_S = 15;
export const CHAINED_REASONS: readonly FwdReason[] = ["busy", "no_reply", "not_reachable"];

const E164 = /^\+[1-9]\d{6,14}$/;

/** Normalise a user-typed number to E.164 (strip spaces/dashes, `00` → `+`); undefined when it is not one. */
export function normalizeE164(input: string): string | undefined {
  let s = input.replace(/[\s\-().]/g, "");
  if (s.startsWith("00")) s = `+${s.slice(2)}`;
  return E164.test(s) ? s : undefined;
}

/** Desired forwarding for a member at `position` in a chain of `length` members. */
export function desiredForwarding(position: number, length: number, next: string | undefined, noReplyTimerS = DEFAULT_NO_REPLY_TIMER_S): FwdState {
  const last = position >= length;
  if (last || !next) {
    return { call_waiting: true, rules: CHAINED_REASONS.map((reason) => ({ reason, enabled: false })) };
  }
  return {
    call_waiting: false,
    rules: CHAINED_REASONS.map((reason) => ({ reason, enabled: true, target: next, ...(reason === "no_reply" ? { no_reply_timer_s: noReplyTimerS } : {}) })),
  };
}

/** Desired state for every member of a group, derived from order alone (never stored). */
export function desiredChain(members: readonly Pick<Member, "position" | "number">[], noReplyTimerS = DEFAULT_NO_REPLY_TIMER_S): Map<number, FwdState> {
  const sorted = [...members].sort((a, b) => a.position - b.position);
  const out = new Map<number, FwdState>();
  sorted.forEach((m, i) => out.set(m.position, desiredForwarding(i + 1, sorted.length, sorted[i + 1]?.number, noReplyTimerS)));
  return out;
}

function ruleFor(state: FwdState, reason: FwdReason): FwdRule | undefined {
  return state.rules.find((r) => r.reason === reason);
}

export interface RuleDiff {
  reason: FwdReason | "call_waiting";
  desired: string;
  actual: string;
  ok: boolean;
}

function describeRule(r: FwdRule | undefined): string {
  if (!r || !r.enabled) return "off";
  return `${r.target ?? "?"}${r.no_reply_timer_s !== undefined ? ` (${r.no_reply_timer_s}s)` : ""}`;
}

/**
 * Compare an actual read-back with the desired state, rule by rule. A `no_reply_timer_s`
 * difference alone is tolerated (operators round timers to 5 s steps); an unconditional
 * forward that is enabled is always a mismatch.
 */
export function diffForwarding(desired: FwdState, actual: FwdState | null): RuleDiff[] {
  const rows: RuleDiff[] = [];
  for (const reason of CHAINED_REASONS) {
    const d = ruleFor(desired, reason);
    const a = actual ? ruleFor(actual, reason) : undefined;
    const dOn = !!d?.enabled;
    const aOn = !!a?.enabled;
    const ok = actual !== null && dOn === aOn && (!dOn || d?.target === a?.target);
    rows.push({ reason, desired: describeRule(d), actual: actual ? describeRule(a) : "—", ok });
  }
  const uncond = actual ? ruleFor(actual, "unconditional") : undefined;
  if (uncond?.enabled) rows.push({ reason: "unconditional", desired: "off", actual: describeRule(uncond), ok: false });
  rows.push({ reason: "call_waiting", desired: desired.call_waiting ? "on" : "off", actual: actual ? (actual.call_waiting ? "on" : "off") : "—", ok: actual !== null && actual.call_waiting === desired.call_waiting });
  return rows;
}

export function matchesDesired(desired: FwdState, actual: FwdState | null): boolean {
  return diffForwarding(desired, actual).every((r) => r.ok);
}

/** What the page should say about a member after a job: the cloud's verdict, cross-checked with the read-back. */
export function effectiveSync(m: Pick<Member, "forwarding">): MemberSync {
  const f = m.forwarding;
  if (f.actual?.source === "unverified") return "unverified";
  if (f.sync === "unverified" || f.sync === "repairing") return f.sync;
  return matchesDesired(f.desired, f.actual) ? "in_sync" : "out_of_sync";
}

export function anyUnverified(members: readonly Pick<Member, "forwarding">[]): boolean {
  return members.some((m) => effectiveSync(m) === "unverified");
}

// ---- guards (mirror the cloud's 422s so the page refuses before the request) -------------

export type GuardCode = "group_full" | "same_phone_hop" | "loop" | "invalid_number" | "licence_not_attested" | "position_taken" | "position_unknown";

export interface GuardFailure {
  code: GuardCode;
  detail: string;
}

export interface AddCandidate {
  number: string;
  deviceId: string | null;
  attested: boolean;
  attestedBy: string;
}

/** Guards for appending a member (Prompt 3 v3 "show a warning, and do not proceed…"). */
export function checkAddGuards(group: Pick<Group, "members" | "main_number">, c: AddCandidate): GuardFailure | undefined {
  if (group.members.length >= MAX_MEMBERS) return { code: "group_full", detail: `a line group has at most ${MAX_MEMBERS} members` };
  const number = normalizeE164(c.number);
  if (!number) return { code: "invalid_number", detail: `"${c.number}" is not an E.164 number (+9715…)` };
  if (group.members.some((m) => m.number === number) || (group.members.length > 0 && number === group.main_number)) {
    return { code: "loop", detail: `${number} is already a member of this group; the chain would loop` };
  }
  // Two SIMs on one phone are still one live call: the predecessor would forward to a phone
  // that is already busy (Part A2, "never points from one SIM to another SIM on the same phone").
  if (c.deviceId && group.members.some((m) => m.device_id === c.deviceId)) {
    return { code: "same_phone_hop", detail: `this phone (${c.deviceId}) already holds member ${group.members.find((m) => m.device_id === c.deviceId)!.position}` };
  }
  if (!c.attested || !c.attestedBy.trim()) return { code: "licence_not_attested", detail: "the SIM must be attested as registered under the business's commercial licence" };
  return undefined;
}

/** Guards for replacing the member at `position` with a (possibly) new number on a new phone. */
export function checkReplaceGuards(group: Pick<Group, "members">, position: number, c: { number: string; deviceId: string | null }): GuardFailure | undefined {
  const target = group.members.find((m) => m.position === position);
  if (!target) return { code: "position_unknown", detail: `no member at position ${position}` };
  const number = normalizeE164(c.number);
  if (!number) return { code: "invalid_number", detail: `"${c.number}" is not an E.164 number (+9715…)` };
  if (group.members.some((m) => m.position !== position && m.number === number)) return { code: "loop", detail: `${number} already belongs to another member; the chain would loop` };
  if (c.deviceId && group.members.some((m) => m.position !== position && m.device_id === c.deviceId)) return { code: "same_phone_hop", detail: `this phone already holds another member of the group` };
  return undefined;
}

/** Guard for a first member: it must hold the group's main number. */
export function checkFirstMember(group: Pick<Group, "members" | "main_number">, number: string): GuardFailure | undefined {
  const n = normalizeE164(number);
  if (!n) return { code: "invalid_number", detail: `"${number}" is not an E.164 number` };
  if (group.members.length > 0) return { code: "position_taken", detail: "this group already has a member 1" };
  if (n !== group.main_number) return { code: "loop", detail: `member 1 must hold the main number ${group.main_number}, not ${n}` };
  return undefined;
}

/** Members whose network state a job touches when a member is appended/removed at `position`. */
export function affectedPositions(members: readonly Pick<Member, "position">[], position: number): number[] {
  const positions = members.map((m) => m.position).sort((a, b) => a - b);
  const idx = positions.indexOf(position);
  const out = new Set<number>([position]);
  if (idx > 0) out.add(positions[idx - 1]!);
  if (idx >= 0 && idx + 1 < positions.length) out.add(positions[idx + 1]!);
  return [...out].sort((a, b) => a - b);
}
